import React, { useEffect, useState } from "react";
import { api, TIER_LABEL } from "../lib/api";
import { Loader2, Mail, Phone, MapPin, ShieldCheck } from "lucide-react";

function fmt(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminLeadsTab() {
    const [data, setData] = useState({ leads: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [tierFilter, setTierFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/admin/leads")
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch(() => { if (!cancelled) setData({ leads: [], total: 0 }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const filtered = tierFilter === "all"
        ? data.leads
        : data.leads.filter((l) => l.attorney_tier === tierFilter);

    const exportCsv = () => {
        const rows = [
            ["created_at", "attorney", "tier", "lead_name", "email", "phone", "state", "preferred_contact", "status", "summary"],
            ...filtered.map((l) => [
                l.created_at, l.attorney_name, l.attorney_tier, l.name, l.email, l.phone || "",
                l.state_code || "", l.preferred_contact || "", l.status, (l.issue_summary || "").replace(/[\r\n]+/g, " "),
            ]),
        ];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-leads">
            <p className="text-sm text-muted-foreground mb-4">
                All lead-contact submissions across the platform. Pro/Elite only. Consent logged on submission.
            </p>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="ll-label mr-2">Tier:</span>
                {["all", "top_spot", "featured", "basic"].map((t) => (
                    <button key={t} onClick={() => setTierFilter(t)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                                tierFilter === t ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40"
                            }`}
                            data-testid={`admin-leads-filter-${t}`}>
                        {t === "all" ? "All" : TIER_LABEL[t] || t}
                    </button>
                ))}
                <button onClick={exportCsv}
                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted/50"
                        data-testid="admin-leads-export">
                    Export CSV
                </button>
            </div>
            {loading && <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="ll-card text-sm text-muted-foreground" data-testid="admin-leads-empty">No leads yet for this filter.</div>
            )}
            <div className="space-y-3">
                {filtered.map((l) => (
                    <div key={l.id} className="ll-card text-sm" data-testid={`admin-lead-${l.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                            <div>
                                <div className="font-serif text-base font-semibold">{l.name}</div>
                                <div className="text-xs text-muted-foreground">{fmt(l.created_at)} · status: {l.status}</div>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span className={`ll-pill tier-${l.attorney_tier}`}>{TIER_LABEL[l.attorney_tier] || l.attorney_tier}</span>
                                <span className="text-muted-foreground">→ {l.attorney_name}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                            <span className="inline-flex items-center gap-1.5 text-primary"><Mail className="h-3.5 w-3.5" /> {l.email}</span>
                            {l.phone && <span className="inline-flex items-center gap-1.5 text-primary"><Phone className="h-3.5 w-3.5" /> {l.phone}</span>}
                            {l.state_code && <span className="inline-flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {l.state_code}</span>}
                            {l.consent && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><ShieldCheck className="h-3 w-3" /> consent logged</span>}
                        </div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{l.issue_summary}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
